declare const CLIENT_BUILD_ID: string;

type ClientUpdateListener = () => void;

export type ClientUpdateCheckResult =
    | "current"
    | "updated"
    | "unavailable";

const listeners = new Set<ClientUpdateListener>();
let updateDetected = false;
let detectedBuildId: string | undefined;
let updateCheckInFlight: Promise<ClientUpdateCheckResult> | undefined;
const clientUpdateNoticeDevelopmentOverrideStorageKey =
    "clientUpdateNoticeDevelopmentOverride";
const developmentOverrideListeners = new Set<(enabled: boolean) => void>();

export const clientUpdateCheckIntervalMs = 10000;

export function SubscribeToClientUpdate(listener: ClientUpdateListener) {
    listeners.add(listener);
    if (updateDetected) listener();

    return () => listeners.delete(listener);
}

export function GetDetectedClientBuildId() {
    return detectedBuildId;
}

export function IsClientUpdateNoticeDevelopmentOverrideEnabled() {
    return (
        localStorage.getItem(clientUpdateNoticeDevelopmentOverrideStorageKey) ===
        "true"
    );
}

export function SetClientUpdateNoticeDevelopmentOverride(enabled: boolean) {
    if (enabled) {
        localStorage.setItem(
            clientUpdateNoticeDevelopmentOverrideStorageKey,
            "true"
        );
    } else {
        localStorage.removeItem(clientUpdateNoticeDevelopmentOverrideStorageKey);
    }

    developmentOverrideListeners.forEach((listener) => listener(enabled));
}

export function SubscribeToClientUpdateNoticeDevelopmentOverride(
    listener: (enabled: boolean) => void
) {
    developmentOverrideListeners.add(listener);

    const handleStorageChange = (event: StorageEvent) => {
        if (event.key === clientUpdateNoticeDevelopmentOverrideStorageKey) {
            listener(IsClientUpdateNoticeDevelopmentOverrideEnabled());
        }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
        developmentOverrideListeners.delete(listener);
        window.removeEventListener("storage", handleStorageChange);
    };
}

export function CheckForClientUpdateAsync(): Promise<ClientUpdateCheckResult> {
    if (updateDetected) {
        return Promise.resolve("updated");
    }

    if (updateCheckInFlight) {
        return updateCheckInFlight;
    }

    updateCheckInFlight = CheckForClientUpdateCoreAsync().finally(() => {
        updateCheckInFlight = undefined;
    });

    return updateCheckInFlight;
}

async function CheckForClientUpdateCoreAsync(): Promise<ClientUpdateCheckResult> {
    try {
        const response = await fetch(
            `/version.json?check=${Date.now()}`,
            { cache: "no-store" }
        );

        if (!response.ok) {
            return "unavailable";
        }

        const version = (await response.json()) as { buildId?: string };

        if (typeof version.buildId !== "string" || !version.buildId) {
            return "unavailable";
        }

        if (version.buildId === CLIENT_BUILD_ID) {
            return "current";
        }

        updateDetected = true;
        detectedBuildId = version.buildId;
        listeners.forEach((listener) => listener());
        return "updated";
    } catch {
        return "unavailable";
    }
}
